import { realtimeSupabase, type Cliente } from '@sheafyield/sync';

/**
 * O mínimo de cliente que estas funções usam. Declarado à parte para os
 * testes poderem passar um dublê de autenticação sem construir um
 * `SupabaseClient` inteiro.
 */
export interface ClienteDeSessao {
  auth: {
    getSession(): Promise<{
      data: { session: { user: { id: string } } | null };
    }>;
    onAuthStateChange(
      cb: (evento: string, sessao: { user: { id: string } } | null) => void,
    ): { data: { subscription: { unsubscribe(): void } } };
  };
}

/** O que `aoMudarSessao` precisa do motor (o motor de verdade tem mais). */
export interface MotorDeSessao {
  aoMudarSessao(usuario: string | null): Promise<void>;
}

/**
 * Fábrica do `realtime` do motor que **segue a sessão**.
 *
 * O canal `postgres_changes` filtra por `user_id`, então só existe com
 * sessão — mas o motor arma o canal uma vez, na partida, quando ainda não
 * há sessão nenhuma na primeira instalação. Resolver a sessão uma única vez
 * ali dentro deixava o canal calado para sempre depois do login, e deixava
 * o canal da conta anterior de pé depois de sair. Por isso quem assina
 * `onAuthStateChange` é esta função: ela arma o canal para o usuário atual,
 * derruba e rearma quando o id muda, e derruba ao sair.
 *
 * O `aoEstado` de um canal já derrubado é engolido de propósito: o `CLOSED`
 * de um desligamento nosso não é falha, e o motor o leria como erro por não
 * ter como saber que a queda foi pedida (quem anuncia isso a ele é
 * `aoMudarSessao`).
 */
export function realtimeQueSegueASessao(
  cliente: Cliente,
): (
  aoMudar: () => void,
  aoEstado?: (estado: string, erro?: Error) => void,
) => () => void {
  return (aoMudar, aoEstado) => {
    let desligar: (() => void) | null = null;
    let usuarioArmado: string | null = null;
    let cancelado = false;

    function derrubar(): void {
      const d = desligar;
      desligar = null;
      usuarioArmado = null;
      d?.();
    }

    function armar(id: string | null): void {
      if (cancelado || id === usuarioArmado) return;
      derrubar();
      if (id === null) return;
      usuarioArmado = id;
      const meu = id;
      desligar = realtimeSupabase(cliente, id)(
        () => {
          if (!cancelado && usuarioArmado === meu) aoMudar();
        },
        (estado, erro) => {
          if (!cancelado && usuarioArmado === meu) aoEstado?.(estado, erro);
        },
      );
    }

    void cliente.auth.getSession().then(({ data }) => {
      armar(data.session?.user.id ?? null);
    });
    const { data } = cliente.auth.onAuthStateChange((_evento, sessao) => {
      armar(sessao?.user.id ?? null);
    });

    return () => {
      cancelado = true;
      data.subscription.unsubscribe();
      derrubar();
    };
  };
}

/**
 * Liga a autenticação ao motor: entrar dispara um ciclo, sair devolve o
 * estado a "sem conta" e trocar de conta faz as duas coisas. Registrar uma
 * única vez (o `iniciarApp` cuida disso): dois ouvintes sincronizariam em
 * dobro para sempre.
 *
 * **A adoção no relogin da mesma conta depende deste ouvinte, e só dele.**
 * Quem escreve `user_id` numa linha local é a escrita (o dono da sessão
 * viva, ou a `CONTA_LOCAL` quando não há sessão), a adoção e a purga do
 * `#assumirODono` do motor, e o pull. O `#assumirODono` corre em todo ciclo
 * cujo dono gravado difere do usuário — e ciclos vêm do intervalo, do
 * `visibilitychange`, do `online` e da mutação local, não só daqui. O caso
 * que **só** este ouvinte alcança é o relogin na mesma conta: ali o ciclo
 * não vê diferença nenhuma, e sem o aviso as linhas escritas enquanto a
 * sessão esteve fora ficam sentinela — subirão carimbadas certo (o carimbo
 * é na saída, ADR-0005), mas a cópia local só será adotada na próxima
 * entrada de conta. Quem mexer neste fluxo tem de manter as três chamadas —
 * `SIGNED_OUT`/expiração com `null`, login e relogin com o id — vivas. Ver
 * ADR-0006.
 */
export function ligarSessaoAoMotor(
  cliente: ClienteDeSessao,
  motor: MotorDeSessao,
): () => void {
  const { data } = cliente.auth.onAuthStateChange((_evento, sessao) => {
    // Sem `await` de propósito: o supabase-js segura o lock de auth durante
    // este callback, e `aoMudarSessao` chama `auth.getSession()` lá dentro;
    // aguardar aqui seria deadlock.
    void motor.aoMudarSessao(sessao?.user.id ?? null);
  });
  return () => {
    data.subscription.unsubscribe();
  };
}
